import React, { useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import ReactResizeDetector from "react-resize-detector";

import Tooltip from "components/Tooltip";

import { Container, Wrapper } from "./styles";

type Props = {
  lines?: number;
  text?: string;
  duration?: [number | null, number | null] | number;
  children: any;
  className?: any;
};

type TrimmedProps = {
  lines?: number;
  className?: any;
  children: any;
  getRef?: (ref: any) => void;
};

const CONTENT_IS_TRIMMED_ACCURACY = 2;

const Trimmed = ({ getRef, lines, children, className }: TrimmedProps) => (
  <Wrapper lines={lines} ref={getRef} className={className}>
    {children}
  </Wrapper>
);

const TrimmedText = observer(({ lines, text, children, duration, className }: Props) => {
  const contentRef = useRef<any>(null);
  const [shouldOpenTooltip, setShouldOpenTooltip] = useState(false);

  const isContentTrimmed = useCallback(() => {
    if (contentRef.current) {
      const { offsetHeight, offsetWidth, scrollHeight, scrollWidth } =
        contentRef.current;

      return (
        Math.abs(scrollHeight - offsetHeight) >= CONTENT_IS_TRIMMED_ACCURACY ||
        Math.abs(scrollWidth - offsetWidth) >= CONTENT_IS_TRIMMED_ACCURACY
      );
    }

    return false;
  }, []);

  useEffect(() => {
    setShouldOpenTooltip(isContentTrimmed());
  }, [isContentTrimmed]);

  const getRef = useCallback((ref: any) => {
    contentRef.current = ref;
  }, []);

  const onResize = useCallback(() => {
    setShouldOpenTooltip(isContentTrimmed());
  }, [isContentTrimmed]);

  if (!lines) return children ?? text;

  return (
    <Tooltip
      content={text}
      shouldOpen={shouldOpenTooltip}
      duration={duration}
      childrenWithEllipsis
    >
      <Container>
        <ReactResizeDetector onResize={onResize}>
          <Trimmed lines={lines} getRef={getRef} className={className}>
            {children}
          </Trimmed>
        </ReactResizeDetector>
      </Container>
    </Tooltip>
  );
});

export default TrimmedText;
